import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Avatar,
  useTheme,
} from '@mui/material';
import {
  Settings,
  FolderOpen,
  Storage,
  Psychology,
  Speed,
  Tune,
  Search,
  Memory,
  DataObject,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';

const FolderConfigDialog = ({
  open,
  onClose,
  folder,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  if (!folder) return null;

  const getIconForConfig = (key) => {
    const iconMap = {
      vectorStore: <Storage />,
      embeddingModel: <Psychology />,
      useBatchMode: <Speed />,
      chunkingStrategy: <Tune />,
      dataSize: <DataObject />,
      chunkSize: <Memory />,
      overlap: <Memory />,
      similarityThreshold: <Search />,
      agenticModel: <Psychology />,
      maxChunkSize: <Memory />,
      splitOnPages: <DataObject />,
      skipHeader: <DataObject />,
      cleanRows: <DataObject />,
      searchType: <Search />,
    };
    return iconMap[key] || <Settings />;
  };

  const getStatusIcon = (value) => {
    if (typeof value === 'boolean') {
      return value ? <CheckCircle color="success" /> : <Cancel color="error" />;
    }
    return null;
  };

  const formatValue = (key, value) => {
    if (typeof value === 'boolean') {
      return value ? 'Enabled' : 'Disabled';
    }
    if (key === 'similarityThreshold') {
      return value?.toString() || '0.5';
    }
    return value?.toString() || 'Not set';
  };

  const basicConfigItems = [
    { key: 'vectorStore', label: 'Vector Store', value: folder.vectorStore },
    { key: 'embeddingModel', label: 'Embedding Model', value: folder.embeddingModel },
    { key: 'useBatchMode', label: 'Batch Processing', value: folder.useBatchMode },
  ];

  const processingConfigItems = [
    { key: 'chunkingStrategy', label: 'Chunking Strategy', value: folder.processingOptions?.chunkingStrategy },
    { key: 'dataSize', label: 'Data Size', value: folder.processingOptions?.dataSize },
    { key: 'chunkSize', label: 'Chunk Size', value: folder.processingOptions?.chunkSize },
    { key: 'overlap', label: 'Overlap', value: folder.processingOptions?.overlap },
    { key: 'similarityThreshold', label: 'Similarity Threshold', value: folder.processingOptions?.similarityThreshold },
    { key: 'agenticModel', label: 'Agentic Model', value: folder.processingOptions?.agenticModel },
    { key: 'maxChunkSize', label: 'Max Chunk Size', value: folder.processingOptions?.maxChunkSize },
    { key: 'splitOnPages', label: 'Split on Pages', value: folder.processingOptions?.splitOnPages },
    { key: 'skipHeader', label: 'Skip Header', value: folder.processingOptions?.skipHeader },
    { key: 'cleanRows', label: 'Clean Rows', value: folder.processingOptions?.cleanRows },
    { key: 'searchType', label: 'Search Type', value: folder.processingOptions?.searchType },
  ];

  const ConfigRow = ({ item }) => (
    <TableRow sx={{ 
      '&:last-child td, &:last-child th': { border: 0 },
      '&:hover': {
        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)'
      }
    }}>
      <TableCell sx={{ 
        py: 1.5, 
        borderBottom: '1px solid', 
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ 
            width: 24, 
            height: 24, 
            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'
          }}>
            {getIconForConfig(item.key)}
          </Avatar>
          <Typography variant="body2" sx={{ 
            fontWeight: 600, 
            color: isDarkMode ? 'white' : 'black'
          }}>
            {item.label}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ 
        py: 1.5, 
        borderBottom: '1px solid', 
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={formatValue(item.key, item.value)}
            size="small"
            color={typeof item.value === 'boolean' ? (item.value ? 'success' : 'default') : 'primary'}
            variant={typeof item.value === 'boolean' ? 'filled' : 'outlined'}
            sx={{ 
              fontSize: '0.75rem',
              fontWeight: 500,
              height: 24,
              '& .MuiChip-label': {
                px: 1.5,
                color: 'white'
              },
              '&.MuiChip-outlined': {
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '& .MuiChip-label': {
                  color: 'white'
                }
              },
              '&.MuiChip-filled': {
                '&.MuiChip-colorPrimary': {
                  bgcolor: isDarkMode ? 'rgba(227, 98, 80, 0.8)' : 'rgba(227, 98, 80, 0.9)',
                  '& .MuiChip-label': {
                    color: 'white'
                  }
                },
                '&.MuiChip-colorSuccess': {
                  bgcolor: isDarkMode ? 'rgba(76, 175, 80, 0.8)' : 'rgba(76, 175, 80, 0.9)',
                  '& .MuiChip-label': {
                    color: 'white'
                  }
                },
                '&.MuiChip-colorDefault': {
                  bgcolor: isDarkMode ? 'rgba(158, 158, 158, 0.8)' : 'rgba(158, 158, 158, 0.9)',
                  '& .MuiChip-label': {
                    color: 'white'
                  }
                }
              }
            }}
          />
          {getStatusIcon(item.value)}
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          boxShadow: isDarkMode 
            ? '0 8px 32px rgba(0,0,0,0.4)' 
            : '0 8px 32px rgba(0,0,0,0.12)',
          bgcolor: isDarkMode ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'
      }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
          <Settings />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: isDarkMode ? 'white' : 'black'
          }}>
            {folder.name} - Configuration
          </Typography>
          <Typography variant="body2" sx={{ 
            color: isDarkMode ? 'white' : 'black',
            fontWeight: 400
          }}>
            Knowledge base folder settings and processing options
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Compact Header */}
        <Box sx={{ 
          p: 2, 
          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)', 
          borderBottom: '1px solid', 
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <FolderOpen />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ 
                mb: 0.5,
                color: isDarkMode ? 'white' : 'black'
              }}>
                {folder.description || 'No description provided'}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: isDarkMode ? 'white' : 'black'
              }}>
                Created: {new Date(folder.created_at).toLocaleDateString()}
              </Typography>
            </Box>
            <Chip
              label="Active"
              color="success"
              size="small"
              sx={{ 
                fontWeight: 500,
                '& .MuiChip-label': {
                  color: 'white'
                }
              }}
            />
          </Box>
        </Box>

        {/* Configuration Table */}
        <TableContainer>
          <Table size="small">
            <TableBody>
              {/* Basic Configuration Section */}
              <TableRow>
                <TableCell colSpan={2} sx={{ 
                  bgcolor: isDarkMode ? 'rgba(227, 98, 80, 0.1)' : 'rgba(227, 98, 80, 0.05)', 
                  py: 1,
                  borderBottom: '2px solid',
                  borderColor: 'primary.main'
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: isDarkMode ? 'white' : 'black',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Basic Configuration
                  </Typography>
                </TableCell>
              </TableRow>
              {basicConfigItems.map((item) => (
                <ConfigRow key={item.key} item={item} />
              ))}
              
              {/* Processing Configuration Section */}
              <TableRow>
                <TableCell colSpan={2} sx={{ 
                  bgcolor: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)', 
                  py: 1,
                  borderBottom: '2px solid',
                  borderColor: 'secondary.main'
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: isDarkMode ? 'white' : 'black',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Processing Options
                  </Typography>
                </TableCell>
              </TableRow>
              {processingConfigItems.map((item) => (
                <ConfigRow key={item.key} item={item} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Compact Summary */}
        <Box sx={{ 
          p: 2, 
          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)', 
          borderTop: '1px solid', 
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
        }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  lineHeight: 1,
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  {folder.processingOptions?.chunkSize || 1000}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.7rem',
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  Chunk Size
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  lineHeight: 1,
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  {folder.processingOptions?.overlap || 200}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.7rem',
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  Overlap
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  lineHeight: 1,
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  {folder.processingOptions?.similarityThreshold || 0.5}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.7rem',
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  Similarity Threshold
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  lineHeight: 1,
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  {folder.useBatchMode ? 'ON' : 'OFF'}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.7rem',
                  color: isDarkMode ? 'white' : 'black'
                }}>
                  Batch Mode
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid', 
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'
      }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          size="small"
          sx={{ 
            minWidth: 100,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderConfigDialog;
